import { map, Observable } from 'rxjs';
import { Component, Input, ChangeDetectionStrategy, OnInit, AfterViewInit, ViewChild } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { JoiEvent, WFWorker } from '../../store/events.era';
import { Subject } from 'rxjs';
import { filteredFrontPageEventsReselector, eventViewReselector, accountEventLabelMapsReselector } from 'src/app/store/reselectors/filteredEvents.reselector';
import { filterActions, filtersFeature, filterTypes } from 'src/app/store/filters.era';
import { FiltersGroupingUI } from '../filters/filters-ui.models';
import { NewEventDialogComponent } from 'src/app/modules/dashboard/dashboard.dialog.newEvent.component';
import { loadLocal, saveLocal, weekDays } from 'src/app/helpers/helpers';
import { User } from 'globals/store/user.data';
import { DashboardPageType } from 'globals/store/user.data';
import { Wrapper } from '../wrapper.component';
import { EventViewService } from 'src/app/services/event.view.service';
import { potentialEventOwnersByEventIdsReselector } from '../../store/reselectors/workforce.reselector';
import { Dictionary } from '@ngrx/entity';
import { unknownDateToDate } from 'globals/helpers/helpers';
import { settingsActions, settingsFeature } from 'src/app/store/settings.era';
import { Label } from 'globals/store/events.data';
import { accountsFeature } from 'src/app/store/accounts.era';
import { MatMenuModule } from '@angular/material/menu';
import { LoaderPaneFragmentComponent } from '../loader/loader-pane-fragment.component';
import { CommonModule } from '@angular/common';
import { IconComponent } from '../icon/icon.component';
import { EventStatusComponent } from './event-status.component';
import { LabelItemComponent } from '../label-marker/label-item.component';
import { traceNames } from 'src/app/services/performance.tracking.data';
import { LabelTierDialogComponent } from '../label-marker/label-tier.dialog.component';
import { PaginatorComponent } from '../nested-table/paginator.component';
import { PotentialOwnersSelector } from './potential-owners.component';
import { userFeature } from 'src/app/store/user.era';

@Component({
    selector: 'event-view',
    templateUrl: './event-view.component.html',
    styleUrls: ['./event-view.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: true,
    imports: [MatMenuModule, PotentialOwnersSelector, LabelTierDialogComponent, PaginatorComponent, LoaderPaneFragmentComponent, CommonModule, IconComponent, EventStatusComponent ],
})
export class EventViewComponent extends Wrapper<EventViewComponent> implements OnInit, AfterViewInit {
  readonly weeks = weekDays
  _file = 'event-view.component.ts';
  @ViewChild(MatSort) sort: MatSort;
  dashboardPageType: DashboardPageType
  traceNames = traceNames
  accounts = {}
  suspendedMap: { [accountId: string]: boolean } = {}

  canChangeEventAccount = false
  eventLabels = {}
  
  is_loading: any = {};
  search_input = '';
  displayedColumns: string[] = ['title', 'date', 'misc', 'actions'];
  m_card_filter;
  m_list_filter;
  today;
  todayDate;
  selectedDate;
  selectedEvent;
  filtered_selectedEvents;
  calendar_month: Date = new Date();
  calendar_days = [];
  weeks_indexes = [];
  weeks_set = {};
  calendar_events = [];
  account_filters: any = {};
  show_my_events = false;
  show_my_workforce_events = false;
  // event: JoiEvent
  show_filter = false
  event_order = { key: 'title', rev: false }
  @Input() filterGroupings: FiltersGroupingUI[]
  public filteredEvents: JoiEvent[] = []
  public visibleEvents: JoiEvent[] = []

  display_invitations: JoiEvent[] = []; // <== TODO: figure out / reimplement
  filterCount$: Observable<number>
  searchKey$: Subject<string> = new Subject<string>();
  user: User
  accountEventLabelMaps: { [accountId: string]: { [labelId: string]: Label } }
  potentialOwnersByEventIds: Dictionary<WFWorker[]>
  pages: number[] = []
  pageIndex = 0
  itemsPerPage = 25

  constructor(public eventViewService: EventViewService) {
    super()
    this.loadItemsPerPage()
    this.selectProp(potentialEventOwnersByEventIdsReselector, null, 'potentialOwnersByEventIds')
    this.selectCallback(filteredFrontPageEventsReselector, null, (filteredEvents: JoiEvent[]) => {
      this.filteredEvents = filteredEvents.filter(e=>!e.is_invited)
      this.getPages()
      this.setVisibleEvents()
    })
    this.subProp(this.store.select(userFeature.selectUser), 'user')
    this.subCallback(this.store.select(settingsFeature.selectUser), user => {
      if (!user.homepage) this.dashboardPageType = 'card'
      else this.dashboardPageType = user.homepage
    })
    this.selectProp(accountEventLabelMapsReselector, null, 'accountEventLabelMaps')
    this.subProp(this.store.select(accountsFeature.selectSuspended), 'suspendedMap')
    this.filterCount$ = this.store.select(filtersFeature.selectHome).pipe(map(filters => filters.count))
    
    this.selectCallback(eventViewReselector, null, (eventView: {
    eventLabelsByEventId: {[id: string]: string[]}}) => {
      this.eventLabels = eventView.eventLabelsByEventId
    })
    this.subCallback(this.store.select(accountsFeature.selectEntities), accounts => {
      this.accounts = accounts
      this.canChangeEventAccount = Object.values(accounts).length > 1
    })
  }

  ngOnInit() {
    this.subObservableWithDebounce(this.searchKey$, 333, searchTerm =>
      this.store.dispatch(filterActions.updateFilter({
        filterPageType: this.filterPageTypes.home,
        filterType: filterTypes.search,
        value: searchTerm
      }))
    )
  }
  
  setVisibleEvents() {
    this.visibleEvents = this.filteredEvents.slice(this.pageIndex*this.itemsPerPage, (this.pageIndex+1)*this.itemsPerPage)
  }

  setPageIndex(index: number = null) {
    if (index !== null) this.pageIndex = index
    if (this.pageIndex > this.pages.length - 1) this.pageIndex = Math.max(this.pages.length - 1, 0) // Set to last page if index beyond last page
    this.setVisibleEvents()
  }

  getPages() {
    const nPages = Math.ceil(this.filteredEvents.length / this.itemsPerPage)
    this.pages = [...Array(nPages).keys() ]
    this.setPageIndex(this.pageIndex)
  }

  loadItemsPerPage() {
    const nItems = loadLocal<number>('EventsPerPage')
    if (nItems) {
      this.itemsPerPage = nItems
    }
  }

  updateItemsPerPage(nItems: number) {
    saveLocal(`EventsPerPage`, nItems)
    this.itemsPerPage = nItems
    this.loadItemsPerPage()
    this.getPages()
    this.setPageIndex()
  }

  trackById(index, item) {
    return item.id
  }

  trackByEvent(index, event: JoiEvent) {
    return event ? event.eventId : index
  }

  trackByIndex(index) {
    return index
  }

  async goToSubscription() {
    const ids = await this.take1(this.store.select('accounts', 'ids'))
    this.router.navigate(['/account', ids[0]])
  }

  ngAfterViewInit() {
    if (this.sort) {
      this.subCallback(this.sort.sortChange, sort => {
        if (sort.active === 'date') {
          this.settingsService.bank.user.event_order_key = 'first_date';
        } else if (sort.active === 'title') {
          this.settingsService.bank.user.event_order_key = 'title';
        }
        this.settingsService.bank.user.event_order_rev = sort.direction === 'desc' ? false : true;
      })
    }
  }

  editEventTitle(event: JoiEvent) {
    this.eventViewService.editEventTitleThroughDialog(event, this.dialog)
  }

  changeEventStatus(event: JoiEvent) {
    this.eventViewService.editEventStatusThroughDialog(event, this.dialog)
  }

  duplicateEvent(ev: JoiEvent){
    this.eventViewService.duplicateEventThroughDialog(ev, this.dialog)  
  }

  changeEventAccount(ev: JoiEvent){
    this.eventViewService.changeEventAccountThroughDialog(ev, this.dialog)
  }

  closeEvent(ev: JoiEvent) {
    this.eventViewService.closeEventThroughDialog(ev, this.dialog)
  }

  changeOwner(event: JoiEvent, newOwner: WFWorker) {
    this.eventService.changeEventOwner(event, newOwner)
  }

  async deleteEvent(ev: JoiEvent, fromElId: string) {
    const rect = this.getRectFromDotMenuId(fromElId)
    this.eventViewService.deleteEventThroughPositionedDialog(ev, this.dialog, rect)
  }

  async leaveWorkforce(ev: JoiEvent, fromElId: string) {
    const rect = this.getRectFromDotMenuId(fromElId)
    this.eventViewService.leaveWorkforceThroughPositionedDialog(ev, this.dialog, rect)
  }
  
  grantWorkforcePrivileges(evt) {
    this.logService.executePromiseLogError(this, this.eventService.grantWorkforcePrivileges(evt), 'grantWorkforcePrivileges', 'There was an error granting workforce permissions')
  }

  addLabel(elementId: string, current_evt: JoiEvent) {
    const labelsRect = this.getRectFromDotMenuId(elementId)
    this.eventViewService.addLabelThroughPositionedDialog(current_evt, this.dialog, labelsRect)
  }

  viewEvent(uIEvent: JoiEvent) {
    if (!uIEvent) { return; }
    this.eventViewService.openViewEventDialog(uIEvent, this.dialog)
  }

  str2day(d: any): Date {
    return unknownDateToDate(d)
  }

  startNewEvent() {
    // TODO:check all the permissions etc...
    this.openNewEventDialog();
  }
  openNewEventDialog() {
    this.dialog.open(NewEventDialogComponent, { width: '480px', disableClose: true });
  }

  updateOrder(type) {
    switch (type) {
        case 'title':
            if (this.event_order.key === 'title') {
                this.event_order.rev = !this.event_order.rev;
            } else {
                this.event_order.key = 'title';
                this.event_order.rev = false;
            }
            break;
        case 'first_date':
            if (this.event_order.key === 'first_date') {
                this.event_order.rev = !this.event_order.rev;
            } else {
                this.event_order.key = 'first_date';
                this.event_order.rev = false;
            }
            break;
    }
    const update_obj = {};
    update_obj['event_order_key'] = this.event_order.key;
    update_obj['event_order_rev'] = this.event_order.rev;
    this.store.dispatch(settingsActions.updateUserSettings(update_obj))
  }

  getRectFromDotMenuId(elementId: string) {
    return document.getElementById(elementId).getBoundingClientRect()
  }

}